import { IconBrain, IconTarget, IconBolt, IconSunrise, IconBell, IconBook, IconMoon, IconDeviceMobile, IconMoonStars } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { PurchaseButton } from "@/components/common/PurchaseButton";
import { formatDiscount } from "@/lib/utils";
import { usePlans } from "@/hooks/use-plans";

const Benefits = ({ onPurchase }: { onPurchase?: (planId: string) => void }) => {
  const { t, i18n } = useTranslation('common');
  const { plans, isLoading } = usePlans();
  const annualPlan = plans.find(plan => plan.id === 'annual');

  const benefits = [
    {
      titleKey: "benefits.content.focus.title",
      descriptionKey: "benefits.content.focus.description",
      icon: IconBrain,
      imageUrl: t('benefits.content.focus.image'),
    },
    {
      titleKey: "benefits.content.time.title",
      descriptionKey: "benefits.content.time.description",
      icon: IconTarget,
      imageUrl: t('benefits.content.time.image'),
    },
    {
      titleKey: "benefits.content.calmness.title",
      descriptionKey: "benefits.content.calmness.description",
      icon: IconBolt,
      imageUrl: t('benefits.content.calmness.image'),
    },
  ];

  return (
    <section className="pt-2 md:pt-4 pb-4 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12 max-w-2xl mx-auto">
          <h3 className="text-xl md:text-3xl text-sky-500 mb-2 font-semibold">
            {t('benefits.subtitle')}
          </h3>
          <h2 className="text-3xl mx-0 md:mx-0 md:text-4xl font-semibold text-slate-900 mb-8">
            {t('benefits.title')}
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col md:flex-row"
            >
              <div className={cn(
                "px-8 pt-6 md:pt-8 pb-5 flex flex-col items-start md:items-start justify-center gap-2 md:gap-4 md:w-3/5",
                index % 2 === 1 && "md:order-2"
              )}>
                <h3 className="text-slate-900 leading-relaxed text-2xl md:text-3xl text-left font-semibold">
                  <span className="decoration-[16px] decoration-yellow-300 underline underline-offset-[-4px]">{t(benefit.titleKey)}</span>
                </h3>
                <div className="text-base md:text-lg text-slate-600 text-left leading-relaxed md:leading-relaxed">
                  {(t(benefit.descriptionKey, { returnObjects: true }) as string[]).map((text, index) => (
                    <p className="py-1.5" key={index}>{text}</p>
                  ))}
                </div>
              </div>

              <img
                src={benefit.imageUrl}
                alt=""
                className={cn(
                  "w-full md:w-2/5 aspect-[4/5] object-cover",
                  index % 2 === 1 && "md:order-1"
                )}
              />
            </div>
          ))}
        </div>

        <div className="mx-auto max-w-md mb-0 mt-12 md:mb-0 leading-normal px-8">
          <p>
            <span className="text-slate-600">{t('benefits.conclusion.line1')}</span>
            <span className="font-semibold text-slate-800">{t('benefits.conclusion.line2', {
              discount: formatDiscount(annualPlan?.pricing.discount?.percentage || 0, i18n.language)
            })}</span>
          </p>
        </div>
      </div>

      {onPurchase && <PurchaseButton onPurchase={onPurchase} />}
    </section>
  );
};

export default Benefits;
